import type OLMap from 'ol/Map';
import Draw, { createBox } from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import { transform } from 'ol/proj';

import type { ExportFormat, SourceDef } from '../types';
import { findTime } from '../catalogue';
import { SJTSK } from '../projections';
import {
  download,
  exportName,
  exportResolution,
  isExportable,
  packageExport,
  planExport,
  runExport,
  type ExportPlan,
} from '../export';
import { clear, el } from './dom';

const cs = (n: number, digits = 0): string =>
  n.toLocaleString('cs', { maximumFractionDigits: digits, minimumFractionDigits: digits });

/** Rough byte estimate so the user knows what they are about to download. */
function estimateBytes(plan: ExportPlan, format: ExportFormat): number {
  const px = plan.width * plan.height;
  return format === 'png' ? px * 1.1 : px * 0.16;
}

function human(bytes: number): string {
  if (bytes > 1e9) return `${cs(bytes / 1e9, 1)} GB`;
  if (bytes > 1e6) return `${cs(bytes / 1e6)} MB`;
  return `${cs(bytes / 1e3)} kB`;
}

export class ExportPanel {
  readonly root = el('div', { class: 'export-panel', attrs: { hidden: 'hidden' } });

  private readonly vector = new VectorSource();
  private readonly layer = new VectorLayer({
    source: this.vector,
    zIndex: 950,
    style: (feature) =>
      feature.get('role') === 'footprint'
        ? new Style({
            stroke: new Stroke({ color: '#ffb340', width: 2 }),
            fill: new Fill({ color: 'rgba(255, 179, 64, 0.12)' }),
          })
        : new Style({ stroke: new Stroke({ color: 'rgba(255,255,255,0.7)', width: 1, lineDash: [6, 4] }) }),
  });

  private draw?: Draw;
  private plan?: ExportPlan;
  private format: ExportFormat = 'jpeg';
  private controller?: AbortController;
  private active = false;

  constructor(
    private readonly map: OLMap,
    private readonly getActive: () => { def: SourceDef; timeId?: string } | undefined,
  ) {
    // Not part of the layer stack: syncLayers() must not sweep it away.
    this.layer.set('chrome', true);
    this.map.addLayer(this.layer);
  }

  get isActive(): boolean {
    return this.active;
  }

  toggle(): void {
    if (this.active) this.stop();
    else this.start();
  }

  start(): void {
    const target = this.getActive();
    this.active = true;
    this.root.hidden = false;
    this.plan = undefined;
    this.vector.clear();

    if (!target || !isExportable(target.def, target.timeId)) {
      this.renderUnsupported(target?.def);
      return;
    }
    this.beginDraw();
    this.renderHint(target.def, target.timeId);
  }

  stop(): void {
    this.active = false;
    this.root.hidden = true;
    this.controller?.abort();
    this.controller = undefined;
    this.endDraw();
    this.vector.clear();
    this.plan = undefined;
  }

  private beginDraw(): void {
    this.endDraw();
    this.draw = new Draw({ source: this.vector, type: 'Circle', geometryFunction: createBox() });
    this.draw.on('drawstart', () => this.vector.clear());
    this.draw.on('drawend', (ev) => {
      const extent = (ev.feature.getGeometry() as Polygon).getExtent();
      // Let the interaction finish before swapping the UI over.
      setTimeout(() => {
        this.endDraw();
        this.buildPlan(extent);
      }, 0);
    });
    this.map.addInteraction(this.draw);
  }

  private endDraw(): void {
    if (this.draw) {
      this.map.removeInteraction(this.draw);
      this.draw = undefined;
    }
  }

  private buildPlan(extent3857: number[]): void {
    const target = this.getActive();
    if (!target) return;
    this.plan = planExport(target.def, target.timeId, extent3857);
    this.showFootprint(this.plan);
    this.render();
  }

  /**
   * The export is a rectangle in Krovak, so on a north-up map it shows as a
   * rotated quad — draw it, so what you get is what you see.
   */
  private showFootprint(plan: ExportPlan): void {
    const [minx, miny, maxx, maxy] = plan.bbox;
    const steps = 8;
    const ring: number[][] = [];
    const edge = (x1: number, y1: number, x2: number, y2: number) => {
      for (let i = 0; i < steps; i++) {
        const t = i / steps;
        ring.push(transform([x1 + (x2 - x1) * t, y1 + (y2 - y1) * t], SJTSK, 'EPSG:3857'));
      }
    };
    edge(minx, miny, maxx, miny);
    edge(maxx, miny, maxx, maxy);
    edge(maxx, maxy, minx, maxy);
    edge(minx, maxy, minx, miny);
    ring.push(ring[0]);

    this.vector.clear();
    const feature = new Feature(new Polygon([ring]));
    feature.set('role', 'footprint');
    this.vector.addFeature(feature);
  }

  private header(title: string): HTMLElement {
    return el('div', { class: 'export-head' }, [
      el('strong', { text: title }),
      el('button', {
        class: 'export-close',
        attrs: { type: 'button', 'aria-label': 'Zavřít export' },
        text: '✕',
        on: { click: () => this.stop() },
      }),
    ]);
  }

  private renderUnsupported(def?: SourceDef): void {
    clear(this.root);
    this.root.append(
      this.header('Export výřezu'),
      el('p', {
        class: 'export-note',
        text: def
          ? `${def.title} publikuje jen Web Mercator, takže z něj nelze vyexportovat ` +
            'originální rastr. Vyberte zdroj ČÚZK, IPR Praha nebo CENIA.'
          : 'Není vybrána žádná vrstva.',
      }),
    );
  }

  private renderHint(def: SourceDef, timeId?: string): void {
    clear(this.root);
    this.root.append(
      this.header('Export výřezu'),
      el('p', { class: 'export-note' }, [
        // Two taps rather than a drag: dragging would fight with panning the map,
        // and this way the view stays usable while choosing the area.
        el('span', { text: 'Klepněte na dva protilehlé rohy výřezu. Export bude z vrstvy ' }),
        el('strong', { text: def.title }),
        el('span', { text: ` v ${cs(exportResolution(def, timeId) * 100, 1)} cm/px.` }),
      ]),
    );
  }

  private render(): void {
    const plan = this.plan;
    if (!plan) return;
    const epoch = plan.timeId ? findTime(plan.def, plan.timeId)?.label : undefined;
    const megapixels = (plan.width * plan.height) / 1e6;

    clear(this.root);
    this.root.append(
      this.header('Export výřezu'),
      el('div', { class: 'export-grid' }, [
        el('span', { class: 'export-key', text: 'Vrstva' }),
        el('span', { text: `${plan.def.title}${epoch ? ` · ${epoch}` : ''}` }),
        el('span', { class: 'export-key', text: 'Rozlišení' }),
        el('span', { text: `${cs(plan.res * 100, 2)} cm/px · nativní` }),
        el('span', { class: 'export-key', text: 'Rozměr' }),
        el('span', {
          text: `${cs(plan.width)} × ${cs(plan.height)} px · ${cs(plan.groundWidth)} × ${cs(plan.groundHeight)} m`,
        }),
        el('span', { class: 'export-key', text: 'Systém' }),
        el('span', { text: 'S-JTSK / Krovak East North (EPSG:5514)' }),
      ]),
    );

    if (plan.huge) {
      this.root.append(
        el('p', {
          class: 'export-warn',
          text:
            `${cs(megapixels)} Mpx, ${cs(plan.chunks.length)} dlaždic — během kódování ` +
            `zabere zhruba ${human(plan.width * plan.height * 4)} paměti a prohlížeč ho ` +
            'nemusí zvládnout. Stáhnout jde i tak.',
        }),
      );
    }

    const formats = el('div', { class: 'export-formats' }, [
      this.formatButton('jpeg', 'JPEG'),
      this.formatButton('png', 'PNG'),
      el('span', { class: 'export-size', text: `≈ ${human(estimateBytes(plan, this.format))}` }),
    ]);

    this.root.append(
      formats,
      el('div', { class: 'export-actions' }, [
        el('button', {
          class: 'export-btn',
          attrs: { type: 'button' },
          text: 'Nakreslit znovu',
          on: { click: () => this.start() },
        }),
        el('button', {
          class: 'export-btn export-btn-primary',
          attrs: { type: 'button' },
          text: 'Stáhnout',
          on: { click: () => void this.run() },
        }),
      ]),
      el('p', {
        class: 'export-note',
        text: 'ZIP obsahuje obrázek, world file a .prj — otevře se v QGIS na správném místě.',
      }),
    );
  }

  private formatButton(format: ExportFormat, label: string): HTMLElement {
    return el('button', {
      class: this.format === format ? 'export-format is-on' : 'export-format',
      attrs: { type: 'button' },
      text: label,
      on: {
        click: () => {
          this.format = format;
          this.render();
        },
      },
    });
  }

  private async run(): Promise<void> {
    const plan = this.plan;
    if (!plan) return;
    this.controller = new AbortController();

    const bar = el('div', { class: 'export-bar-fill' });
    const status = el('p', { class: 'export-note', text: 'Stahuji dlaždice…' });
    clear(this.root);
    this.root.append(
      this.header('Export výřezu'),
      status,
      el('div', { class: 'export-bar' }, [bar]),
      el('button', {
        class: 'export-btn',
        attrs: { type: 'button' },
        text: 'Zrušit',
        on: { click: () => this.controller?.abort() },
      }),
    );

    try {
      const image = await runExport(
        plan,
        this.format,
        ({ done, total }) => {
          bar.style.width = `${Math.round((done / total) * 100)}%`;
          status.textContent = `Stahuji dlaždice… ${done}/${total}`;
        },
        this.controller.signal,
      );
      status.textContent = 'Balím archiv…';
      const zip = await packageExport(plan, image, this.format);
      download(zip, `${exportName(plan)}.zip`);
      status.textContent = `Hotovo — ${human(zip.size)}`;
      bar.style.width = '100%';
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        this.render();
        return;
      }
      clear(this.root);
      this.root.append(
        this.header('Export výřezu'),
        el('p', { class: 'export-warn', text: `Export selhal: ${(err as Error).message}` }),
        el('button', {
          class: 'export-btn',
          attrs: { type: 'button' },
          text: 'Zkusit znovu',
          on: { click: () => void this.run() },
        }),
      );
    } finally {
      this.controller = undefined;
    }
  }
}
